import React, { useState } from "react";
import { bool, func, string } from "prop-types";
import { signImage, uploadS3ToTemp } from "@quintype/bridgekeeper-js";

import { Icon } from "../../atoms/icon";
import { ImageCropperModal } from "../image-cropper-modal";
import "./profile-image-upload-modal.m.css";
import UploadIcon from "../../../../assets/icons/upload";
import CameraIcon from "../../../../assets/icons/camera";
import DeleteIcon from "../../../../assets/icons/delete";
import GalleryIcon from "../../../../assets/icons/gallery";
import { PROFILE_IMAGE_UPLOAD_CONTENT, PROFILE_IMAGE_FORM_DATA_KEYS } from "../../../constants";

export const ProfileImageUploadModal = ({
  isOpen,
  onClose,
  onImageUpload,
  className = "",
  hasImage,
  onRemoveClick,
  ...props
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  if (!isOpen) return null;

  const prepareFormData = (keys, imageList, res) => {
    const formData = new FormData();
    keys.forEach((key) => {
      key === "file" ? formData.append(key, imageList[0]) : formData.append(key, res[key]);
    });
    return formData;
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError(PROFILE_IMAGE_UPLOAD_CONTENT.INVALID_FILE_ERROR);
      return;
    }

    setError(null);
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setSelectedImageSrc(reader.result);
      setOriginalFile(file);
      setIsCropperOpen(true);
    });
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    setSelectedImageSrc(null);
    setOriginalFile(null);
    setIsCropperOpen(false);
    onClose();
  };

  const handleCroppedFile = async (croppedFile) => {
    setIsCropperOpen(false);
    setIsLoading(true);
    setError(null);

    const keys = PROFILE_IMAGE_FORM_DATA_KEYS;

    try {
      const signedImage = await signImage(croppedFile.name, croppedFile.type);
      const formData = prepareFormData(keys, [croppedFile], signedImage);
      const uploadedImage = await uploadS3ToTemp(signedImage.action, formData);

      if (uploadedImage.status === 201) {
        onImageUpload({
          file: croppedFile,
          tempImageKey: signedImage.key,
          uploadedImage
        });
        handleClose();
      } else {
        throw new Error(PROFILE_IMAGE_UPLOAD_CONTENT.UPLOAD_ERROR);
      }
    } catch (err) {
      console.error('Image upload error:', err);
      setError(PROFILE_IMAGE_UPLOAD_CONTENT.UPLOAD_FAILED_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleTakePhoto = () => {
    const cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.capture = 'camera';
    cameraInput.style.display = 'none';

    cameraInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleFileSelect(file);
      }
      document.body.removeChild(cameraInput);
    };

    document.body.appendChild(cameraInput);
    cameraInput.click();
  };

  const handleSelectFromGallery = () => {
    document.getElementById('photo-input').click();
  };

  const handleRemovePhoto = () => {
    if (onRemoveClick) {
      onRemoveClick();
    }
    handleClose();
  };

  return (
    <div styleName="modal-overlay" className={className} onClick={handleOverlayClick} {...props}>
      <div styleName="modal-content">
        <button styleName="close-button" onClick={handleClose} type="button">
          <Icon name="close" size="xsmall" />
        </button>

        {/* Desktop/Tablet View */}
        <div styleName="desktop-view">
          <div styleName="modal-header">
            <h2 styleName="modal-title">{PROFILE_IMAGE_UPLOAD_CONTENT.MODAL_TITLE}</h2>
          </div>

          <div styleName="modal-body">
            {error && (
              <div styleName="error-message">
                {error}
              </div>
            )}

            <div
              styleName={`upload-area ${dragOver ? 'drag-over' : ''} ${isLoading ? 'loading' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div styleName="placeholder-image">
                <img
                  src="/icons/placeholder_image.png"
                  alt={PROFILE_IMAGE_UPLOAD_CONTENT.PLACEHOLDER_ALT}
                  styleName="placeholder-img"
                />
              </div>
              {isLoading ? (
                <p styleName="upload-text">{PROFILE_IMAGE_UPLOAD_CONTENT.UPLOADING_TEXT}</p>
              ) : (
                <p styleName="upload-text">{PROFILE_IMAGE_UPLOAD_CONTENT.DRAG_TEXT}</p>
              )}
            </div>

            <button
              type="button"
              onClick={handleSelectFromGallery}
              styleName="upload-button"
              disabled={isLoading}
            >
              <UploadIcon color="black" />
              {isLoading ? PROFILE_IMAGE_UPLOAD_CONTENT.UPLOADING_BUTTON : PROFILE_IMAGE_UPLOAD_CONTENT.UPLOAD_BUTTON}
            </button>
          </div>
        </div>

        {/* Mobile View */}
        <div styleName="mobile-view">
          <div styleName="mobile-header">
            <h2 styleName="mobile-title">{PROFILE_IMAGE_UPLOAD_CONTENT.MODAL_TITLE}</h2>
          </div>

          <div styleName="mobile-options">
            {error && (
              <div styleName="error-message">
                {error}
              </div>
            )}

            <button
              styleName="option-button"
              onClick={handleTakePhoto}
              disabled={isLoading}
            >
              <CameraIcon color="#8F8F8F" width="16" height="16" />
              <span styleName="option-text">
                {PROFILE_IMAGE_UPLOAD_CONTENT.TAKE_PHOTO}
              </span>
            </button>

            <button
              styleName="option-button"
              onClick={handleSelectFromGallery}
              disabled={isLoading}
            >
              <GalleryIcon />
              <span styleName="option-text">
                {PROFILE_IMAGE_UPLOAD_CONTENT.SELECT_GALLERY}
              </span>
            </button>

            <button styleName={`option-button ${!hasImage ? 'disabled' : ''}`} onClick={handleRemovePhoto}>
              <DeleteIcon />
              <span styleName={`option-text ${!hasImage ? 'disabled' : ''}`}>{PROFILE_IMAGE_UPLOAD_CONTENT.REMOVE_PHOTO}</span>
            </button>
          </div>
        </div>

        <input
          id="photo-input"
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </div>

      <ImageCropperModal
        isOpen={isCropperOpen}
        imageSrc={selectedImageSrc}
        originalFile={originalFile}
        onClose={() => setIsCropperOpen(false)}
        onCropComplete={handleCroppedFile}
      />
    </div>
  );
};

ProfileImageUploadModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onImageUpload: func,
  className: string
};